export async function fastFileHash(
  file: File,
  sampleSize = 512 * 1024,
  sampleCount = 8
): Promise<string> {
  const start = performance.now()
  const subtle = globalThis.crypto?.subtle

  if (!subtle) {
    throw new Error('WebCrypto is not available')
  }

  const logTime = () => {
    const ms = performance.now() - start
    const sizeMB = file.size / 1024 / 1024

    console.log(
      `[fastFileHash] ${file.name} | ${sizeMB.toFixed(1)} MB | ${ms.toFixed(0)} ms (${(ms / 1000).toFixed(2)}s)`
    )
  }

  // For small files, just hash the whole thing.
  const totalSampleBytes = sampleSize * sampleCount

  if (file.size <= totalSampleBytes) {
    const buffer = await file.arrayBuffer()
    const digest = await subtle.digest('SHA-1', buffer)

    logTime()
    return toHex(new Uint8Array(digest))
  }

  const maxStart = Math.max(0, file.size - sampleSize)

  const positions = Array.from(
    { length: sampleCount },
    (_, index) => {
      if (sampleCount <= 1) return 0
      return Math.floor(maxStart * (index / (sampleCount - 1)))
    }
  )

  const chunks = await Promise.all(
    positions.map(async position => {
      const end = Math.min(file.size, position + sampleSize)
      const buffer = await file.slice(position, end).arrayBuffer()

      return {
        position,
        bytes: new Uint8Array(buffer)
      }
    })
  )

  const encoder = new TextEncoder()

  const metadata = chunks.map(chunk =>
    encoder.encode(`${chunk.position}:`)
  )

  const header = encoder.encode(
    `sampled-v1:${file.size}:${sampleSize}:${sampleCount}:`
  )

  const totalLength =
    header.length +
    chunks.reduce(
      (sum, chunk, index) =>
        sum + metadata[index].length + chunk.bytes.length,
      0
    )

  const input = new Uint8Array(totalLength)

  let offset = 0

  input.set(header, offset)
  offset += header.length

  for (let index = 0; index < chunks.length; index++) {
    input.set(metadata[index], offset)
    offset += metadata[index].length

    input.set(chunks[index].bytes, offset)
    offset += chunks[index].bytes.length
  }

  const digest = await subtle.digest('SHA-1', input)

  logTime()
  return toHex(new Uint8Array(digest))
}

function toHex(bytes: Uint8Array): string {
  let output = ''

  for (let index = 0; index < bytes.length; index++) {
    output += bytes[index].toString(16).padStart(2, '0')
  }

  return output
}